import { makeStyles, Theme } from "@material-ui/core";

type Props = {
  background: string;
  bg_mobile: string;
};

const useStyles = makeStyles<Theme, Props>((theme: Theme) => ({
  pageRoot: {
    minHeight: "100vh",
    width: "100vw",
    display: "flex",
    backgroundColor: "#F8FAFC",
    overflow: "hidden",
  },

  // Left Hero Section
  heroSection: {
    flex: 1.2,
    background: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #311B92 100%)",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: "60px 80px",
    color: "#FFFFFF",
    overflow: "hidden",
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },
  heroGlow1: {
    position: "absolute",
    top: "-20%",
    left: "-10%",
    width: 500,
    height: 500,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(79, 70, 229, 0.4) 0%, rgba(0,0,0,0) 70%)",
    pointerEvents: "none",
  },
  heroGlow2: {
    position: "absolute",
    bottom: "-10%",
    right: "-10%",
    width: 600,
    height: 600,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(129, 140, 248, 0.25) 0%, rgba(0,0,0,0) 70%)",
    pointerEvents: "none",
  },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(12px)",
    padding: "8px 18px",
    borderRadius: 100,
    fontSize: "0.85rem",
    fontWeight: 800,
    color: "#A5B4FC",
    letterSpacing: "0.06em",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    width: "fit-content",
    zIndex: 2,
  },
  heroContent: {
    zIndex: 2,
    maxWidth: 580,
  },
  heroTitle: {
    fontSize: "2.75rem",
    fontWeight: 900,
    lineHeight: 1.2,
    letterSpacing: "-0.02em",
    marginBottom: 20,
    background: "linear-gradient(180deg, #FFFFFF 0%, #CBD5E1 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  heroSubtitle: {
    fontSize: "1.1rem",
    lineHeight: 1.7,
    color: "#94A3B8",
    marginBottom: 40,
  },
  featureGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  featureCard: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "16px 20px",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    backdropFilter: "blur(8px)",
    borderRadius: 16,
    border: "1px solid rgba(255, 255, 255, 0.08)",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      transform: "translateX(6px)",
    },
  },
  featureIconBox: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: "rgba(79, 70, 229, 0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#818CF8",
  },
  featureTitle: {
    fontSize: "0.95rem",
    fontWeight: 800,
    color: "#F8FAFC",
  },
  featureDesc: {
    fontSize: "0.825rem",
    color: "#94A3B8",
  },
  heroFooter: {
    zIndex: 2,
    fontSize: "0.85rem",
    color: "#64748B",
  },

  // Right Form Section
  formSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "40px",
    position: "relative",
    backgroundColor: "#FFFFFF",
    [theme.breakpoints.down("sm")]: {
      padding: "24px 16px",
      width: "100%",
    },
  },
  languageWrapper: {
    position: "absolute",
    top: 32,
    right: 40,
    zIndex: 10,
    [theme.breakpoints.down("sm")]: {
      top: 16,
      right: 16,
    },
  },
  selectLanguage: {
    minWidth: 130,
    "& .MuiOutlinedInput-root": {
      borderRadius: 100,
      backgroundColor: "#F8FAFC",
      border: "1px solid #E2E8F0",
      height: 40,
      fontSize: "0.85rem",
      fontWeight: 700,
    },
    "& .MuiSelect-selectMenu": {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 16px",
    },
  },
  icon__flag: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    objectFit: "cover",
  },

  formCard: {
    width: "100%",
    maxWidth: 440,
  },
  formTitle: {
    fontSize: "2rem",
    fontWeight: 900,
    color: "#0F172A",
    letterSpacing: "-0.02em",
    marginBottom: 8,
  },
  formSubtitle: {
    fontSize: "0.95rem",
    color: "#64748B",
    marginBottom: 32,
  },

  formLabel: {
    fontSize: "0.85rem",
    fontWeight: 700,
    color: "#334155",
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  fieldWrapper: {
    marginBottom: 20,
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px !important",
      backgroundColor: "#F8FAFC !important",
      height: "48px !important",
      transition: "all 0.2s ease",
      "& fieldset": {
        borderColor: "#CBD5E1 !important",
        borderWidth: "1.5px !important",
      },
      "&:hover fieldset": {
        borderColor: "#94A3B8 !important",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#4F46E5 !important",
        borderWidth: "2px !important",
      },
    },
    "& .MuiInputBase-input": {
      padding: "12px 16px !important",
      fontSize: "0.95rem",
      fontWeight: 600,
      color: "#0F172A",
    },
    "& .MuiInputLabel-root": {
      display: "none !important",
    },
  },

  optionsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 28,
  },
  checkboxRemember: {
    "& .MuiTypography-root": {
      fontSize: "0.875rem",
      fontWeight: 600,
      color: "#475569",
    },
    "& .MuiCheckbox-root": {
      color: "#94A3B8",
      "&.Mui-checked": {
        color: "#4F46E5",
      },
    },
  },
  forgotPasswordLink: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: "#4F46E5",
    cursor: "pointer",
    transition: "color 0.2s ease",
    "&:hover": {
      color: "#3730A3",
      textDecoration: "underline",
    },
  },

  btnSubmit: {
    width: "100%",
    height: 48,
    borderRadius: 12,
    backgroundColor: "#4F46E5",
    color: "#FFFFFF",
    fontSize: "1rem",
    fontWeight: 800,
    textTransform: "none",
    boxShadow: "0 8px 20px rgba(79, 70, 229, 0.3)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#4338CA",
      boxShadow: "0 10px 24px rgba(79, 70, 229, 0.4)",
    },
  },

  registerPrompt: {
    marginTop: 28,
    textAlign: "center",
    fontSize: "0.9rem",
    color: "#64748B",
    "& span": {
      color: "#4F46E5",
      fontWeight: 800,
      cursor: "pointer",
      marginLeft: 6,
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },

  // Legacy fallback rules
  background: {},
  content: {},
  icon__flag_legacy: {},
  form_legacy: {},
  form__title: {},
  checkbox__rememberMe: {},
  forgotPassword: {},
  btn_submit: {},
  textRegister: {},
  background_mobile: {},
  form_login_mobile: {},
  selectEmpty: {},
}));

export default useStyles;
